import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ControllerModal,
  DialogContent,
  ControllerAlert,
  TextField,
  Stack,
  Typography,
  DialogActions,
  Button,
} from '@bymason-private/legacy-pattern-components';
import { useToast } from '../../../..';
import { UserModel, useDeleteUser } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';

interface DeleteUserModalProps {
  open: boolean;
  user: UserModel;
  onClose: () => void;
  onSuccess?: (user: UserModel) => void;
}

export function DeleteUserModal({ onClose, onSuccess, open, user }: DeleteUserModalProps) {
  const [setToastMessage, setToastSeverity] = useToast();
  const { authToken = '' } = useAuth();
  const [deleteUser, deleteError, deleteBusy] = useDeleteUser(authToken);

  const [pendingDelete, setPendingDelete] = useState(false);
  const [email, setEmail] = useState('');
  const enableDelete = useMemo(() => email && email === user.email, [email, user.email]);

  const resetFormState = () => {
    setEmail('');
  };

  const handleDelete = useCallback(() => {
    deleteUser(user.user_id);
    setPendingDelete(true);
  }, [deleteUser, user.user_id]);

  const handleEmailChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(evt.target.value);
  };

  useEffect(() => {
    if (!pendingDelete || deleteBusy) {
      return;
    }
    if (!deleteError) {
      setToastMessage(`Successfully deleted ${user.name} (${user.email})`);
      setToastSeverity('success');
    } else {
      setToastSeverity('error');
      setToastMessage(deleteError);
    }
    setPendingDelete(false);
    onSuccess && onSuccess(user);
  }, [
    pendingDelete,
    setPendingDelete,
    deleteBusy,
    deleteError,
    setToastMessage,
    setToastSeverity,
    user,
    onSuccess,
  ]);

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open]);

  return (
    <ControllerModal onClose={onClose} open={open} title={`Delete ${user.name}`}>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <Typography>
            To confirm deletion, please type in the email address for the user{' '}
            <strong>
              {user.name} ({user.email})
            </strong>
          </Typography>
          <ControllerAlert
            sx={{
              width: '100%',
              mt: 2,
            }}
            variant='outlined'
            severity='error'
          >
            All API Keys associated with this user will be deleted along with the user{' '}
          </ControllerAlert>
          <TextField value={email} autoFocus onChange={handleEmailChange} required fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='error'
          onClick={handleDelete}
          size='medium'
          variant='contained'
          disabled={!enableDelete}
        >
          Delete User
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
